import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProtectedRoute, { PermissionRoute, SuperAdminRoute } from "@/components/ProtectedRoute";
import AdminLayout from "@/components/AdminLayout";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import StatementOfFaithPage from "@/pages/StatementOfFaithPage";
import FounderPage from "@/pages/FounderPage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import GalleryPage from "@/pages/GalleryPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import ContactPage from "@/pages/ContactPage";
import AdminLogin from "@/admin/AdminLogin";
import AdminDashboard from "@/admin/AdminDashboard";
import AdminContent from "@/admin/AdminContent";
import AdminGallery from "@/admin/AdminGallery";
import AdminBlog from "@/admin/AdminBlog";
import AdminFounder from "@/admin/AdminFounder";
import AdminContactEditor from "@/admin/AdminContactEditor";
import AdminSettings from "@/admin/AdminSettings";
import AdminMessages from "@/admin/AdminMessages";
import AdminActivityRegistrations from "@/admin/AdminActivityRegistrations";
import AdminUsers from "@/admin/AdminUsers";
import AdminStripeSettings from "@/admin/AdminStripeSettings";
import AdminDonations from "@/admin/AdminDonations";
import AdminFooterSettings from "@/admin/AdminFooterSettings";
import DonationPage from "@/pages/DonationPage";
import DonationSuccessPage from "@/pages/DonationSuccessPage";
import { warmPublicRouteCache } from "@/lib/publicPageData";
import ScrollToTopButton from "@/components/ScrollToTopButton";

function PublicLayout() {
  useEffect(() => {
    const warm = () => warmPublicRouteCache();
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(warm, { timeout: 1500 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(warm, 250);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen"><Outlet /></main>
      <Footer />
      <ScrollToTopButton />
    </>
  );
}

function InitialRouteRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname !== "/") return;

    const params = new URLSearchParams(location.search);
    const redirectTarget = params.get("redirect");
    if (!redirectTarget || !redirectTarget.startsWith("/")) return;

    navigate(redirectTarget, { replace: true });
  }, [location.pathname, location.search, navigate]);

  return null;
}

function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <InitialRouteRedirect />
        <ScrollToTop />
        <Routes>
          {/* Public Pages */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/statement-of-faith" element={<StatementOfFaithPage />} />
            <Route path="/founder" element={<FounderPage />} />
            <Route path="/activities" element={<ActivitiesPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPostPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/donate" element={<DonationPage />} />
            <Route path="/donate/success" element={<DonationSuccessPage />} />
          </Route>

          {/* Admin Login */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Admin Dashboard */}
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="content" element={<PermissionRoute permission="content"><AdminContent /></PermissionRoute>} />
            <Route path="gallery" element={<PermissionRoute permission="gallery"><AdminGallery /></PermissionRoute>} />
            <Route path="blog" element={<PermissionRoute permission="blog"><AdminBlog /></PermissionRoute>} />
            <Route path="founder" element={<PermissionRoute permission="founder"><AdminFounder /></PermissionRoute>} />
            <Route path="contact" element={<PermissionRoute permission="contact"><AdminContactEditor /></PermissionRoute>} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="messages" element={<PermissionRoute permission="messages"><AdminMessages /></PermissionRoute>} />
            <Route path="activity-registrations" element={<PermissionRoute permission="activity_registrations"><AdminActivityRegistrations /></PermissionRoute>} />
            <Route path="users" element={<SuperAdminRoute><AdminUsers /></SuperAdminRoute>} />
            <Route path="stripe" element={<SuperAdminRoute><AdminStripeSettings /></SuperAdminRoute>} />
            <Route path="donations" element={<PermissionRoute permission="donations"><AdminDonations /></PermissionRoute>} />
            <Route path="footer-settings" element={<SuperAdminRoute><AdminFooterSettings /></SuperAdminRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
